/**
 * Text measurement without a DOM.
 *
 * A chart is laid out during render — on the server for the first paint, and on every
 * keystroke after that — so it cannot ask the browser how wide a label is. These
 * functions estimate it from the Helvetica/Arial advance widths instead, which is what
 * the charts are drawn in (`CHART_FONT`). The estimate is within a few percent for Latin
 * text and deliberately errs wide, so a margin sized from it never clips a label.
 */

/** The typeface every chart is drawn in. Sans-serif, as APA 7 asks for figure text. */
export const CHART_FONT = "Arial, Helvetica, sans-serif";

/** For monospace text — the stem-and-leaf display. */
export const CHART_MONO_FONT =
  "ui-monospace, Menlo, Consolas, 'Liberation Mono', 'Courier New', monospace";

/** Advance width of a monospace character, as a fraction of the font size. */
export const MONO_CHAR_WIDTH = 0.6;

/**
 * Helvetica advance widths for U+0020–U+007E, in thousandths of an em (Adobe's
 * Helvetica AFM; Arial is metrically compatible).
 */
const ASCII_WIDTHS = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278,
  278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584,
  584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556,
  833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278,
  278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222,
  500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500,
  500, 334, 260, 334, 584,
] as const;

/** Symbols that turn up in chart labels, with their widths. */
const SYMBOL_WIDTHS: Record<string, number> = {
  "−": 584, // minus sign
  "–": 556, // en dash
  "—": 1000, // em dash
  "…": 1000, // ellipsis
  "×": 584, // multiplication sign
  "≤": 549,
  "≥": 549,
  "²": 333,
  "°": 400,
  " ": 278,
  " ": 200,
};

const WIDE_SCRIPT = /[ᄀ-ᇿ⺀-鿿가-힯豈-﫿＀-￯]/u;
const COMBINING_MARK = /\p{M}/u;

/** Bold Helvetica runs about 5–8% wider; erring wide keeps labels unclipped. */
const BOLD_FACTOR = 1.08;

function charUnits(char: string): number {
  const code = char.codePointAt(0) ?? 32;
  if (code >= 32 && code <= 126) {
    return ASCII_WIDTHS[code - 32];
  }
  const symbol = SYMBOL_WIDTHS[char];
  if (symbol !== undefined) {
    return symbol;
  }
  if (COMBINING_MARK.test(char)) {
    return 0;
  }
  if (WIDE_SCRIPT.test(char)) {
    return 1000;
  }
  // Accented Latin, Greek, Cyrillic and the rest: a little wider than the average
  // lowercase letter.
  return 600;
}

export interface TextStyle {
  fontSize: number;
  bold?: boolean;
  /** Measure as monospace (every character `MONO_CHAR_WIDTH` em). */
  mono?: boolean;
}

/** Estimated rendered width of `text`, in the same units as `fontSize`. */
export function estimateTextWidth(
  text: string,
  { fontSize, bold = false, mono = false }: TextStyle
): number {
  if (mono) {
    return [...text].length * MONO_CHAR_WIDTH * fontSize;
  }
  let units = 0;
  for (const char of text) {
    units += charUnits(char);
  }
  return (units / 1000) * fontSize * (bold ? BOLD_FACTOR : 1);
}

const ELLIPSIS = "…";

/** `text`, shortened with an ellipsis if it is wider than `maxWidth`. */
export function truncateText(
  text: string,
  maxWidth: number,
  style: TextStyle
): string {
  if (estimateTextWidth(text, style) <= maxWidth) {
    return text;
  }
  const chars = [...text];
  let low = 0;
  let high = chars.length;
  // Longest prefix that still fits with the ellipsis after it.
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    const candidate = `${chars.slice(0, middle).join("").trimEnd()}${ELLIPSIS}`;
    if (estimateTextWidth(candidate, style) <= maxWidth) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  return low === 0
    ? ELLIPSIS
    : `${chars.slice(0, low).join("").trimEnd()}${ELLIPSIS}`;
}

const WHITESPACE = /\s+/;

/** Break one word that is wider than a line into pieces that fit. */
function breakWord(word: string, maxWidth: number, style: TextStyle): string[] {
  const pieces: string[] = [];
  let current = "";
  for (const char of word) {
    if (current && estimateTextWidth(current + char, style) > maxWidth) {
      pieces.push(current);
      current = char;
    } else {
      current += char;
    }
  }
  if (current) {
    pieces.push(current);
  }
  return pieces;
}

/**
 * Greedy word wrap into lines no wider than `maxWidth`. With `maxLines`, the last
 * line is truncated with an ellipsis when the text does not fit.
 */
export function wrapText(
  text: string,
  maxWidth: number,
  style: TextStyle,
  maxLines = Number.POSITIVE_INFINITY
): string[] {
  const words = text.trim().split(WHITESPACE).filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (estimateTextWidth(candidate, style) <= maxWidth) {
      current = candidate;
      continue;
    }
    if (current) {
      lines.push(current);
    }
    if (estimateTextWidth(word, style) <= maxWidth) {
      current = word;
      continue;
    }
    const pieces = breakWord(word, maxWidth, style);
    current = pieces.pop() ?? "";
    lines.push(...pieces);
  }
  if (current) {
    lines.push(current);
  }
  if (lines.length <= maxLines) {
    return lines;
  }
  const kept = lines.slice(0, Math.max(1, maxLines));
  const rest = lines.slice(kept.length - 1).join(" ");
  kept[kept.length - 1] = truncateText(`${rest}${ELLIPSIS}`, maxWidth, style);
  return kept;
}

/** The widest of several labels. */
export function maxTextWidth(
  labels: readonly string[],
  style: TextStyle
): number {
  let widest = 0;
  for (const label of labels) {
    widest = Math.max(widest, estimateTextWidth(label, style));
  }
  return widest;
}
